import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { configure } from 'mobx';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line jira/restricted/@atlaskit+flag
import { FlagsProvider, useFlags } from '@atlaskit/flag';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { ContextualAnalyticsData, FireUIAnalytics, SCREEN } from '@atlassian/analytics-bridge';
import { useIntl } from '@atlassian/jira-intl';
import type { MediaUploadAndViewContexts } from '@atlassian/jsm-attachment';
import { useFormSaveErrorsActions } from '@atlassian/proforma-common-core/form-system';
import type { FormAnswers, FormStatus } from '@atlassian/proforma-common-core/form-system-models';
import { ErrorBoundary } from '@atlassian/proforma-common-core/jira-common';
import {
	type MediaContextProps,
	MediaProvider,
	PfFlagsProvider,
	UtilsProvider,
} from '@atlassian/proforma-common-core/jira-common-context';
import type { ApiError, BackendSettings } from '@atlassian/proforma-common-core/jira-common-models';
import { createUtils } from '@atlassian/proforma-common-core/jira-common-utils';
import { FormIndexStoreContainer } from '@atlassian/proforma-form-system';
import { Action, ActionSubject, AnalyticsProvider } from '@atlassian/proforma-utils/analytics';
import { NoticeProvider } from '@atlassian/proforma-utils/show-notice';
import type { ProjectType } from '@atlassian/proforma-utils/types';
import { useUFOComponentExperience } from '@atlassian/ufo';
import { LiveIssueViewFormApi } from '../../apis/live-issue-view-form-api.tsx';
import { IssueViewFormApiProvider } from '../../common/api-provider/index.tsx';
import { adminPermissions, editPermissions, readOnlyPermissions } from '../../common/constants.tsx';
import { IssueViewStore } from '../../stores/issue-view-store.tsx';
import { IssueView } from './IssueView.tsx';
import { issueViewAttachedFormExperience } from './jira-form-card-index/experiences.tsx';
import type { IssueViewModuleContext } from './types.tsx';
import { convertToFormIndexItems } from './utils.tsx';

// Configure MobX to isolate itself from other copies of MobX, such as the MobX loaded by proforma-ui or a third-party Jira app.
configure({ isolateGlobalState: true });

export interface IssueViewSettings {
	accountId: string;
	projectId: number;
	projectType: ProjectType;
	issueKey: string;
	issueId: number;
	userCanEditIssue?: boolean;
	userIsProjectAdmin?: boolean;
	showFormVisibility?: boolean;
	intlLocale: string;
	editModeCallback: (isFormInEditMode: boolean) => void;
}

interface IssueViewBaseAppProps {
	settings: BackendSettings<IssueViewSettings>;
	moduleContext: IssueViewModuleContext;
	addFormButtonClickedCount: number;
}

export const IssueViewBaseApp = ({
	settings,
	moduleContext,
	addFormButtonClickedCount,
}: IssueViewBaseAppProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { set: setFormSaveErrors, clear: clearFormSaveErrors } = useFormSaveErrorsActions();
	const intl = useIntl();
	const [issueViewStore, setIssueViewStore] = useState<IssueViewStore>();
	const { showFlag } = useFlags();

	useEffect(() => {
		setIssueViewStore(
			new IssueViewStore(
				intl,
				moduleContext,
				settings.context.projectId,
				settings.context.issueKey,
				settings.context.issueId,
				createAnalyticsEvent,
				clearFormSaveErrors,
				settings.context.editModeCallback,
				showFlag,
			),
		);
		// NOTE: `intl` from `useIntl()` must not be used in a dependency list.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		clearFormSaveErrors,
		createAnalyticsEvent,
		setFormSaveErrors,
		moduleContext,
		settings.context.issueId,
		settings.context.issueKey,
		settings.context.projectId,
	]);

	const getFormIndex = useCallback(async () => {
		const formIndex = await moduleContext.apis.issueViewFormApi.getFormIndex(
			settings.context.issueId,
		);

		return convertToFormIndexItems(formIndex);
	}, [moduleContext.apis.issueViewFormApi, settings.context.issueId]);

	const deleteForm = useCallback(
		(formId: number) =>
			moduleContext.apis.issueViewFormApi.deleteForm(settings.context.issueId, formId),
		[moduleContext.apis.issueViewFormApi, settings.context.issueId],
	);
	const reopenForm = useCallback(
		(formId: number, formStatus?: FormStatus) =>
			moduleContext.apis.issueViewFormApi.reopenForm(settings.context.issueId, formId, formStatus),
		[moduleContext.apis.issueViewFormApi, settings.context.issueId],
	);

	const updateFormVisibility = useCallback(
		(formId: number, isInternal: boolean) =>
			moduleContext.apis.issueViewFormApi.updateFormVisibility(
				settings.context.issueId,
				formId,
				isInternal,
			),
		[moduleContext.apis.issueViewFormApi, settings.context.issueId],
	);

	const submitForm = useCallback(
		(formId: number, submitLock?: boolean) =>
			moduleContext.apis.issueViewFormApi.submitForm(settings.context.issueId, formId, submitLock),
		[moduleContext.apis.issueViewFormApi, settings.context.issueId],
	);

	const saveAnswers = useCallback(
		(formId: number, formAnswers: FormAnswers) =>
			moduleContext.apis.issueViewFormApi.saveAnswers(
				settings.context.issueId,
				formId,
				formAnswers,
			),
		[moduleContext.apis.issueViewFormApi, settings.context.issueId],
	);

	const hasSeenOnboarding = useCallback(
		(propertyKey: string) =>
			moduleContext.apis.issueViewFormApi
				.getUserProperty(propertyKey, settings.context.accountId)
				.then((_: unknown) => true)
				.catch((error: ApiError) => error.status !== 404),
		[moduleContext.apis.issueViewFormApi, settings],
	);

	const markAsSeenOnboarding = useCallback(
		(propertyKey: string) =>
			moduleContext.apis.issueViewFormApi.setUserProperty(
				propertyKey,
				settings.context.accountId,
				true,
			),
		[moduleContext.apis.issueViewFormApi, settings],
	);

	const api = useMemo(
		() => ({
			getFormIndex,
			deleteForm,
			updateFormVisibility,
			reopenForm,
			submitForm,
			saveAnswers,
			hasSeenOnboarding,
			markAsSeenOnboarding,
		}),
		[
			getFormIndex,
			deleteForm,
			updateFormVisibility,
			reopenForm,
			submitForm,
			saveAnswers,
			hasSeenOnboarding,
			markAsSeenOnboarding,
		],
	);

	return (
		<ErrorBoundary errorUtils={moduleContext.utils.errorUtils}>
			<NoticeProvider>
				<PfFlagsProvider flags={settings.flags}>
					<UtilsProvider utils={moduleContext.utils}>
						{issueViewStore && (
							<>
								<IssueViewFormApiProvider api={moduleContext.apis.issueViewFormApi}>
									<FormIndexStoreContainer
										api={api}
										errorUtils={moduleContext.utils.errorUtils}
										projectId={settings.context.projectId}
										permissions={moduleContext.permissions}
										scope={settings.context.issueId.toString()}
									>
										<IssueView
											proformaUrl={settings.urls.api}
											issueViewStore={issueViewStore}
											permissions={moduleContext.permissions}
											showFormVisibility={settings.context.showFormVisibility || false}
											addFormButtonClickedCount={addFormButtonClickedCount}
											disableIssueViewFullPageDropzoneCallback={settings.context.editModeCallback}
											projectType={settings.context.projectType}
										/>
									</FormIndexStoreContainer>
								</IssueViewFormApiProvider>
								<FireUIAnalytics
									actionSubject={ActionSubject.PANEL}
									action={Action.VIEWED}
									actionSubjectId="proformaFormSystem"
								/>
							</>
						)}
						<ModalTransition>
							<div id="pf-dialog-container" />
						</ModalTransition>
					</UtilsProvider>
				</PfFlagsProvider>
			</NoticeProvider>
		</ErrorBoundary>
	);
};

type IssueViewAppProps = {
	settings: BackendSettings<IssueViewSettings>;
	addFormButtonClickedCount: number;
	mediaContextDetails?: MediaUploadAndViewContexts;
	// eslint-disable-next-line jira/react/handler-naming
	mediaReadTokenRefresh?: () => void;
};

/**
 * Since moduleContext is dependent on settings, we need to store and update them together
 * so the children don't render in an incomplete state when settings updates.
 */
interface IssueViewConfiguration {
	settings: BackendSettings<IssueViewSettings>;
	moduleContext: IssueViewModuleContext;
}

const WrappedIssueViewApp = ({ settings, addFormButtonClickedCount }: IssueViewAppProps) => {
	const { showFlag } = useFlags();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useUFOComponentExperience(issueViewAttachedFormExperience);

	const [configuration, setConfiguration] = useState<IssueViewConfiguration>();
	useEffect(() => {
		const utils = createUtils(
			settings,
			'IssueView',
			showFlag,
			createAnalyticsEvent,
			settings.context.intlLocale,
		);
		let permissions = readOnlyPermissions;
		if (settings.context.userIsProjectAdmin && settings.context.userCanEditIssue) {
			permissions = adminPermissions;
		} else if (settings.context.userCanEditIssue) {
			permissions = editPermissions;
		}
		setConfiguration({
			settings,
			moduleContext: {
				permissions,
				apis: {
					issueViewFormApi: new LiveIssueViewFormApi(
						utils.apiUtil,
						settings.context.projectId,
						settings.context.issueId,
					),
				},
				utils: {
					errorUtils: utils.errorUtils,
				},
			},
		});
	}, [createAnalyticsEvent, settings, showFlag]);

	return configuration ? (
		<IssueViewBaseApp
			settings={configuration.settings}
			moduleContext={configuration.moduleContext}
			addFormButtonClickedCount={addFormButtonClickedCount}
		/>
	) : (
		<></>
	);
};

/**
 * When using this component, ensure:
 *  * it is wrapped by an `AnalyticsListener`, if you want to listen to analytics events
 *  * the `settings` property is static and does not change unless necessary (e.g. wrap it in `useState`)
 */
export const IssueViewApp: React.FC<IssueViewAppProps> = (props) => {
	const mediaContext: MediaContextProps = {
		mediaUploadAndViewContexts: props.mediaContextDetails,
		projectId: props.settings.context.projectId,
		issueKey: props.settings.context.issueKey,
		mediaReadTokenRefresh: props.mediaReadTokenRefresh,
	};
	return (
		<AnalyticsProvider {...props.settings.analytics}>
			<ContextualAnalyticsData
				sourceName="proformaJiraIssueView"
				sourceType={SCREEN}
				attributes={getAnalyticsAttributes(props.settings.context)}
			>
				<FlagsProvider>
					<MediaProvider mediaContext={mediaContext}>
						<WrappedIssueViewApp {...props} />
					</MediaProvider>
				</FlagsProvider>
			</ContextualAnalyticsData>
		</AnalyticsProvider>
	);
};

const getAnalyticsAttributes = (settings: IssueViewSettings) => {
	const {
		projectId,
		projectType,
		issueId,
		userIsProjectAdmin,
		userCanEditIssue,
		showFormVisibility,
	} = settings;

	return {
		projectId,
		projectType,
		issueId,
		isIssueEditableByUser: userCanEditIssue || false,
		isProjectAdmin: userIsProjectAdmin || false,
		isFormVisibilityShown: showFormVisibility || false,
	};
};
